import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from "../firebaseConfig";
import emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import '../styles/modelDetails.scss';

interface Model {
  name: string;
  category: string;
  height: string;
  bust: string;
  waist: string;
  hips: string;
  shoeSize: string;
  eyes: string;
  hair: string;
  images: string[];
  instagramHandle: string;
}

export default function ModelDetailsPage() {
  const { id } = useParams();
  const [model, setModel] = useState<Model | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    const fetchModel = async () => {
      if (!id) return;
      const docRef = doc(db, 'models', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) setModel(docSnap.data() as Model);
    };
    fetchModel();
  }, [id]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    emailjs.send('service_ylew5z7', 'template_hga44pf', {
      name: formData.name,
      email: formData.email,
      message: formData.message,
      model_name: model?.name,
    }, 'E3DeVs5pgEBhATod5')
      .then(() => {
        setShowBookingForm(false);
        setFormData({ name: '', email: '', message: '' });
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      })
      .catch(() => alert("Failed to send booking."))
      .finally(() => setIsSending(false));
  };

  if (!model) return <div className="loading">Loading...</div>;

  return (
    <div className="model-detail-container">
      <div className="left-column">
        {model.images.map((img, i) => (
          <div className="img-wrapper" key={i} onClick={() => setSelectedImage(img)}>
            <img src={img} alt={`${model.name} ${i + 1}`} />
          </div>
        ))}
      </div>

      <div className="right-column">
        <h1>{model.name}</h1>
        <p className="category">{model.category}</p>

        {model.instagramHandle && (
          <a
            href={`https://instagram.com/${model.instagramHandle.replace('@', '')}`}
            target="_blank"
            rel="noreferrer"
            className="instagram-link"
          >
            <FontAwesomeIcon icon={faInstagram} style={{ marginRight: '6px' }} />
            @{model.instagramHandle.replace('@', '')}
          </a>
        )}

        <div className="stats">
          <p><strong>Height:</strong> {model.height}</p>
          <p><strong>Bust:</strong> {model.bust}</p>
          <p><strong>Waist:</strong> {model.waist}</p>
          <p><strong>Hips:</strong> {model.hips}</p>
          <p><strong>Shoe Size:</strong> {model.shoeSize}</p>
          <p><strong>Eyes:</strong> {model.eyes}</p>
          <p><strong>Hair:</strong> {model.hair}</p>
        </div>

        <button className="book-btn" onClick={() => setShowBookingForm(true)}>Book This Model</button>
      </div>

      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="Large view" />
          <button className="close-btn" onClick={() => setSelectedImage(null)}>×</button>
        </div>
      )}

      {showBookingForm && (
        <div className="booking-modal" onClick={() => setShowBookingForm(false)}>
          <div className="form-container" onClick={(e) => e.stopPropagation()}>
            <h2>Book {model.name}</h2>
            <form onSubmit={handleFormSubmit}>
              <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleFormChange} required />
              <input type="email" name="email" placeholder="Your Email" value={formData.email} onChange={handleFormChange} required />
              <textarea name="message" placeholder="Message" value={formData.message} onChange={handleFormChange} required />
              <button type="submit" disabled={isSending}>{isSending ? "Sending..." : "Send Request"}</button>
            </form>
            <button className="close-btn" onClick={() => setShowBookingForm(false)}>×</button>
          </div>
        </div>
      )}

      {showSuccessMessage && <div className="success-message">Booking request sent! 🎉</div>}
    </div>
  );
}